import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { QuestionCircleOutlined } from "@ant-design/icons";
import { Icon } from "@iconify/react";
import { Button, Card, List, Popconfirm, Table, Tooltip } from "antd";
import { ColumnsType } from "antd/es/table";
import { toast } from "utils/toast";

import { useDeleteProperty } from "hooks/useDeleteProperty";

import PropertyActionSelect from "js/components/Properties/PropertiesTable/PropertyActionSelect";
import { PropertiesTableProps } from "js/components/Properties/types";
import { Property } from "js/components/types";

import "styles/pages/Properties/PropertyForm.scss";

const PropertiesTable: React.FC<PropertiesTableProps> = ({
  data,
  loading,
  pagination,
  setPagination,
  fromOrganization = false,
  setIsPropertyModalOpen,
  setPropertyId,
  filteredData,
  screen,
  setFilteredData,
}) => {
  const navigate = useNavigate();

  const deleteMutation = useDeleteProperty();
  const getPropertyDetailsPath = (propertyId?: string | null) => {
    const pathPropertyId = propertyId ?? "";
    return fromOrganization
      ? `/organizations/properties/${pathPropertyId}/details`
      : `/properties/${pathPropertyId}/details`;
  };

  const handleDeleteProperty = (property: Property) => {
    deleteMutation.mutate(property.id!, {
      onSuccess: () => {
        toast.success("Inmueble eliminado correctamente");
      },
      onError: () => {
        toast.error("No se pudo eliminar el inmueble.");
      },
    });
  };

  const columns: ColumnsType<Property> = [
    {
      title: "Alias",
      dataIndex: "alias",
      render: (alias) => alias ?? "-",
      width: "4%",
    },
    {
      title: "Dirección",
      dataIndex: "address",
      render: (address) => <span className="sentry-mask">{address.street}</span>,
      width: "8%",
    },
    {
      title: "Colonia",
      dataIndex: "address",
      render: (address) => <span className="sentry-mask">{address.neighborhood}</span>,
      width: "5%",
    },
    {
      title: "Ciudad",
      dataIndex: "address",
      render: (address) => <span className="sentry-mask">{address.city}</span>,
      width: "5%",
    },
    {
      title: "Fecha de Creación",
      dataIndex: "created_at",
      render: (date) =>
        new Date(date).toLocaleDateString("es-ES", {
          year: "numeric",
          month: "long",
          day: "numeric",
        }),
      width: "6%",
    },
  ];

  if (!fromOrganization)
    columns.push({
      title: "Acciones",
      dataIndex: "actions",
      align: "center",
      render: (_, property) => (
        <PropertyActionSelect
          property={property}
          setIsPropertyModalOpen={setIsPropertyModalOpen || (() => null)}
          setPropertyId={setPropertyId || (() => null)}
        />
      ),
      width: "5%",
    });

  const handleTableChange = (pagination: any) => {
    setPagination({
      current: pagination.current || 1,
      pageSize: pagination.pageSize || 15,
      total: pagination.total || 0,
    });
  };

  useEffect(() => {
    if (data.length) {
      setFilteredData(data);
    }
  }, [data]);

  return (
    <>
      {!screen.md ? (
        <>
          <List
            grid={{
              gutter: 16,
              xs: 1,
              sm: 2,
              md: 4,
              lg: 4,
              xl: 4,
              xxl: 3,
            }}
            dataSource={filteredData?.length ? filteredData : data}
            renderItem={(item) => (
              <List.Item key={item.id}>
                <Card
                  title={item.alias ? `Alias: ${item.alias}` : "Alias:"}
                  onClick={() => navigate(getPropertyDetailsPath(item.id))}
                  actions={[
                    <Tooltip title="Actualizar datos de Inmueble">
                      <Button
                        size={"small"}
                        type={"link"}
                        onClick={(e) => {
                          e?.stopPropagation();
                          if (setIsPropertyModalOpen)
                            setIsPropertyModalOpen(true);
                          if (setPropertyId) setPropertyId(item.id || "");
                        }}
                      >
                        <Icon fontSize={20} icon="akar-icons:edit" />
                      </Button>
                    </Tooltip>,
                    <Tooltip title="Eliminar Inmueble">
                      <Popconfirm
                        title="Eliminar"
                        description="¿Seguro que deseas eliminar?"
                        onConfirm={(e) => {
                          e?.stopPropagation();
                          handleDeleteProperty(item);
                        }}
                        onCancel={(e) => e?.stopPropagation()}
                        icon={
                          <QuestionCircleOutlined
                            style={{ color: "red" }}
                            rev={undefined}
                          />
                        }
                        okText={"Ok"}
                        cancelText={"Cancelar"}
                      >
                        <Button
                          danger
                          size={"small"}
                          type={"link"}
                          onClick={(e) => e?.stopPropagation()}
                        >
                          <Icon fontSize={20} icon="bi:trash3" />
                        </Button>
                      </Popconfirm>
                    </Tooltip>,
                  ]}
                >
                  <span style={{ fontWeight: "bold" }}>Dirección: </span>
                  <span className="sentry-mask">
                    {item.address?.street} {item.address?.exterior_number}
                  </span>
                  <br />
                  <span style={{ fontWeight: "bold" }}>Colonia: </span>
                  <span className="sentry-mask">{item.address?.neighborhood}</span>,
                  <br />
                  <span style={{ fontWeight: "bold" }}>Ciudad: </span>
                  <span className="sentry-mask">{item.address?.city}, {item.address?.state}</span>
                  <br />
                  <span style={{ fontWeight: "bold" }}>Código Postal: </span>
                  <span className="sentry-mask">{item.address?.zip_code}</span>
                  <br />
                </Card>
              </List.Item>
            )}
          />
        </>
      ) : (
        <>
          <Table
            size={"small"}
            columns={columns}
            rowKey={(record) => record.id ?? ""}
            dataSource={filteredData?.length ? filteredData : data}
            pagination={pagination}
            loading={loading || deleteMutation.isPending}
            onChange={handleTableChange}
            scroll={{ y: 750, x: "max-content" }}
            onRow={(row) => ({
              onClick: () => {
                navigate(getPropertyDetailsPath(row.id));
              },
              style: { cursor: "pointer" },
              className: "properties-table-row",
            })}
            components={{
              header: {
                cell: (props: any) => (
                  <th
                    {...props}
                    style={{
                      textAlign: props.children.includes("Acciones")
                        ? "center"
                        : undefined,
                    }}
                  />
                ),
              },
            }}
          />
        </>
      )}
    </>
  );
};

export default PropertiesTable;
